import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  LabelList,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import styled from 'styled-components';
import { getStatusLabel } from '../../services/statusService';
import { getAppOriginLabel } from '../../services/appOriginService';
import { getDemandTypeLabel } from '../../services/demandTypeService';
import ChartCard, { ChartLegend, ChartTooltip, TruncatedTick } from './ChartCard';
import {
  ANIMATION,
  AXIS_TICK_SM,
  demandTypeColor,
  formatNumber,
  toCount,
  useChartAnimation,
} from './chartTheme';

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
`;

const Tile = styled.article`
  min-width: 0;
  padding: 0.875rem 1rem 0.5rem;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-md);
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--color-border-secondary);
  }
`;

const TileHeader = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`;

const TileTitle = styled.h3`
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 600;
`;

const TileTotal = styled.span`
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: 0.75rem;

  strong {
    color: var(--color-text-primary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const TileEmpty = styled.p`
  margin: 0;
  padding: 1.5rem 0 1.25rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
`;

export const APP_STATUS_KEYS = ['OPEN', 'IN_PROGRESS', 'PENDING'];

export const ALL_DEMAND_TYPE_KEYS = ['SUPPORT', 'IMPROVEMENT', 'PROJECT', 'OTHER'];

export const resolveDemandTypeSeriesKeys = (filter) => {
  if (Array.isArray(filter) && filter.length > 0) {
    const selected = new Set(filter.map((t) => String(t).toUpperCase()));
    return ALL_DEMAND_TYPE_KEYS.filter((key) => selected.has(key));
  }
  return ALL_DEMAND_TYPE_KEYS;
};

export const parseTotalByAppList = (res) => {
  const rawList = res?.content ?? res?.data ?? res;
  return Array.isArray(rawList) ? rawList : [];
};

const countForOrigin = (list, origin) => {
  const match = (Array.isArray(list) ? list : []).find(
    (row) => String(row?.appOrigin ?? '').toLowerCase() === String(origin).toLowerCase()
  );
  return toCount(match?.count);
};

export const buildAppStatusRows = (origins, byStatusAndType, seriesKeys) => {
  const codes = Array.isArray(origins) ? origins : [];
  const types = Array.isArray(seriesKeys) && seriesKeys.length > 0 ? seriesKeys : ALL_DEMAND_TYPE_KEYS;
  return codes.map((origin) => {
    const row = {
      appOrigin: origin,
      appLabel: getAppOriginLabel(origin),
    };
    APP_STATUS_KEYS.forEach((status) => {
      const bucket = {};
      types.forEach((type) => {
        bucket[type] = countForOrigin(byStatusAndType?.[status]?.[type], origin);
      });
      row[status] = bucket;
    });
    return row;
  });
};

const ROW_HEIGHT = 32;

const TotalTicketsByAppStatusChart = ({
  data = [],
  loading = false,
  error = false,
  onRetry = null,
  demandTypeFilter = [],
}) => {
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const seriesKeys = useMemo(
    () => resolveDemandTypeSeriesKeys(demandTypeFilter),
    [demandTypeFilter]
  );

  const { sections, grandTotal, maxRowTotal } = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    let grand = 0;
    let maxRow = 0;
    const built = list.map((app) => {
      const appOrigin = app?.appOrigin ?? '';
      const appLabel = app?.appLabel ?? String(appOrigin);
      let appTotal = 0;
      const rows = APP_STATUS_KEYS.map((status) => {
        const counts = app?.[status] && typeof app[status] === 'object' ? app[status] : {};
        const row = {
          statusCode: status,
          statusLabel: getStatusLabel(status),
          total: 0,
        };
        seriesKeys.forEach((type) => {
          row[type] = toCount(counts[type]);
          row.total += row[type];
        });
        appTotal += row.total;
        maxRow = Math.max(maxRow, row.total);
        return row;
      });
      grand += appTotal;
      return { appOrigin, appLabel, rows, total: appTotal };
    });
    built.sort((a, b) => b.total - a.total);
    return { sections: built, grandTotal: grand, maxRowTotal: maxRow };
  }, [data, seriesKeys]);

  const legendItems = seriesKeys.map((key, index) => ({
    key,
    label: getDemandTypeLabel(key),
    color: demandTypeColor(key, index),
  }));

  const renderTile = (section) => (
    <Tile key={section.appOrigin}>
      <TileHeader>
        <TileTitle title={section.appLabel}>{section.appLabel}</TileTitle>
        <TileTotal>
          <strong>{formatNumber(section.total)}</strong> ativos
        </TileTotal>
      </TileHeader>
      {section.total === 0 ? (
        <TileEmpty>Nenhum ticket ativo.</TileEmpty>
      ) : (
        <ResponsiveContainer width="100%" height={section.rows.length * ROW_HEIGHT + 8}>
          <BarChart
            data={section.rows}
            layout="vertical"
            margin={{ top: 4, right: 36, left: 0, bottom: 4 }}
            barCategoryGap="26%"
          >
            <XAxis type="number" hide domain={[0, Math.max(1, maxRowTotal)]} />
            <YAxis
              type="category"
              dataKey="statusLabel"
              width={112}
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={<TruncatedTick maxChars={15} />}
            />
            <Tooltip
              cursor={{ fill: 'var(--chart-cursor)' }}
              content={
                <ChartTooltip
                  nameFormatter={getDemandTypeLabel}
                  labelFormatter={(label) => `${section.appLabel} · ${label}`}
                  showTotal
                  hideZero
                />
              }
            />
            {seriesKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                stackId="demandType"
                fill={demandTypeColor(key, index)}
                fillOpacity={activeKey && activeKey !== key ? 0.25 : 1}
                radius={index === seriesKeys.length - 1 ? [0, 4, 4, 0] : 0}
                maxBarSize={16}
                isAnimationActive={animate}
                {...ANIMATION}
              >
                {index === seriesKeys.length - 1 ? (
                  <LabelList
                    dataKey="total"
                    position="right"
                    offset={8}
                    formatter={formatNumber}
                    style={{ ...AXIS_TICK_SM, fill: 'var(--color-text-secondary)', fontWeight: 600 }}
                  />
                ) : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Tile>
  );

  return (
    <ChartCard
      title="Tickets ativos por aplicação"
      description="Abertos, em andamento e pendentes · mesma escala em todas as aplicações"
      stat={grandTotal}
      statLabel="tickets ativos"
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={sections.length === 0}
      emptyTitle="Nenhuma aplicação encontrada"
      emptyDescription="Cadastre origens de app nas configurações para acompanhar este painel."
      skeleton="tiles"
    >
      {seriesKeys.length > 1 ? (
        <ChartLegend
          items={legendItems}
          activeKey={activeKey}
          onActiveChange={setActiveKey}
          label="Tipos de demanda"
        />
      ) : null}
      <TilesGrid>{sections.map(renderTile)}</TilesGrid>
    </ChartCard>
  );
};

TotalTicketsByAppStatusChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      appOrigin: PropTypes.string,
      appLabel: PropTypes.string,
      OPEN: PropTypes.object,
      IN_PROGRESS: PropTypes.object,
      PENDING: PropTypes.object,
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  demandTypeFilter: PropTypes.arrayOf(PropTypes.string),
};

export default TotalTicketsByAppStatusChart;
